import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Truck, Megaphone, Loader2, LogOut } from "lucide-react";

const REFRESH_MS = 15000;
// LOTE L-1 (item 3): 60 s por slide no carrossel (antes 8 s).
const PAGE_MS = 60000;

const fmtDataBR = (iso) => {
  if (!iso) return "";
  const [y, m, d] = String(iso).split("-");
  return y && m && d ? `${d}/${m}/${y}` : iso;
};
const fmtDataHojePt = (d) => {
  const s = d.toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long", year: "numeric" });
  return s.charAt(0).toUpperCase() + s.slice(1);
};

// LOTE L-2: devolve a etiqueta "Ontem" ou nome do dia da semana (ex.: "Sexta")
// quando a linha é do último dia útil anterior ao `hoje_iso` do feed.
// Regra: se `data_iso === dia_ant_iso` E `dia_ant_iso` é literalmente
// `hoje - 1 dia civil` → "Ontem"; caso contrário (segunda olhando p/ sexta
// / sábado-domingo) → nome do dia da semana ("Sexta" etc.).
const DIAS_SEMANA = ["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"];
function labelDiaAnterior(data_iso, hoje_iso, dia_ant_iso) {
  if (!data_iso || !dia_ant_iso || data_iso !== dia_ant_iso) return null;
  const [hy, hm, hd] = String(hoje_iso).split("-").map(Number);
  const [py, pm, pd] = String(dia_ant_iso).split("-").map(Number);
  if (!hy || !py) return "Ontem";
  const diff = Date.UTC(hy, hm - 1, hd) - Date.UTC(py, pm - 1, pd);
  if (diff === 86400000) return "Ontem";
  const wd = new Date(Date.UTC(py, pm - 1, pd)).getUTCDay();
  return DIAS_SEMANA[wd] || "Ontem";
}

function OntemChip({ label, testid }) {
  if (!label) return null;
  return (
    <span data-testid={testid} className="inline-flex items-center px-2 py-0.5 rounded bg-white/15 text-white/80 text-[14px] font-semibold ml-2 align-middle">
      {label}
    </span>
  );
}

function StatusPill({ status }) {
  const map = {
    "Agendado": "bg-slate-500 text-white",
    "Em trânsito": "bg-blue-600 text-white",
    "Chegou": "bg-amber-500 text-white",
    "Conferido": "bg-indigo-600 text-white",
    "Liberado para o depósito": "bg-emerald-500 text-white",
  };
  return (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-[17px] font-bold whitespace-nowrap ${map[status] || "bg-slate-500 text-white"}`}>
      {status}
    </span>
  );
}

// Título do slide + caminhão animado (Truck do lucide-react). O ícone estático
// ao lado da palavra foi removido — sobra apenas o caminhão em movimento.
function SlideTitleWithTruck({ label, direction, dataTestidPrefix }) {
  const wordRef = useRef(null);
  const [wordLeftPx, setWordLeftPx] = useState(null);

  useLayoutEffect(() => {
    const measure = () => {
      if (!wordRef.current) return;
      const r = wordRef.current.getBoundingClientRect();
      setWordLeftPx(r.left);
    };
    measure();
    window.addEventListener("resize", measure);
    // Remedeia após um tick para pegar layout final quando fontes carregam.
    const t = setTimeout(measure, 300);
    return () => { window.removeEventListener("resize", measure); clearTimeout(t); };
  }, [label]);

  const truckSize = 122;
  // Deixa uma folga visível (~meia largura de letra) entre a frente da cabine
  // e o "R"/"E". Nunca encostar na letra.
  const stopLeft = wordLeftPx != null ? `${wordLeftPx - truckSize - 60}px` : "20vw";
  const cssVars = {
    ["--truck-stop"]: stopLeft,
    ["--truck-size"]: `${truckSize}px`,
  };

  const animName = direction === "in-left" ? "tv-truck-in-left" : "tv-truck-out-left";

  return (
    <div className="relative w-full h-32 flex items-center justify-center pointer-events-none" style={cssVars}>
      {/* Caminhão animado */}
      <div className="absolute inset-0 overflow-hidden">
        <div
          data-testid={`${dataTestidPrefix}-truck`}
          style={{
            position: "absolute",
            top: "50%",
            transform: "translateY(-50%)",
            animation: `${animName} 7s linear infinite`,
          }}
        >
          <div style={{ transform: direction === "out-left" ? "scaleX(-1)" : "none" }}>
            <Truck size={truckSize} strokeWidth={1.5} color="white" />
          </div>
        </div>
      </div>

      <h1 className="relative text-7xl xl:text-8xl font-black tracking-wide z-10">
        <span ref={wordRef}>{label}</span>
      </h1>

      <style>{`
        @keyframes tv-truck-in-left {
          /* Entra pela esquerda; ao ATINGIR o ponto (com folga do "R") some
             imediatamente com fade rápido (~0.3s). Sem ré, sem pausa parado. */
          0%   { left: -180px; opacity: 0; }
          6%   { left: -180px; opacity: 1; }
          40%  { left: var(--truck-stop); opacity: 1; }
          45%  { left: var(--truck-stop); opacity: 0; }
          100% { left: -180px; opacity: 0; }
        }
        @keyframes tv-truck-out-left {
          0%   { left: var(--truck-stop); opacity: 1; }
          55%  { left: -180px; opacity: 1; }
          65%  { left: -180px; opacity: 0; }
          100% { left: -180px; opacity: 0; }
        }
      `}</style>
    </div>
  );
}

// Hook de auto-paginação por altura da tela.
function useAutoPagination(rows, rowHeight, extraOffset = 320) {
  const [perPage, setPerPage] = useState(1);
  useEffect(() => {
    const calc = () => {
      const usable = Math.max(rowHeight, window.innerHeight - extraOffset);
      setPerPage(Math.max(1, Math.floor(usable / rowHeight)));
    };
    calc();
    window.addEventListener("resize", calc);
    return () => window.removeEventListener("resize", calc);
  }, [rowHeight, extraOffset]);
  const pages = Math.max(1, Math.ceil(rows.length / perPage));
  return { perPage, pages };
}

function RecebimentosSlide({ rows, page, onPagesChange, hoje, diaAntIso }) {
  const { perPage, pages } = useAutoPagination(rows, 76, 380);
  useEffect(() => { onPagesChange(pages); }, [pages, onPagesChange]);
  const p = page % pages;
  const visible = rows.slice(p * perPage, p * perPage + perPage);

  // LOTE L-1 (item 2): 12 colunas — nova "EMP" à esquerda de "Data";
  // SKU/NF/KG/Vol/Paletes estreitas; Fornecedor/Observações elásticas.
  const cols = "70px 145px 140px 105px minmax(300px, 3fr) 155px 110px 85px 85px 100px 200px minmax(180px, 1.4fr)";

  return (
    <div className="w-full h-full px-8 pt-2 pb-28 flex flex-col text-white">
      <SlideTitleWithTruck label="RECEBIMENTOS" direction="in-left" dataTestidPrefix="tv-rec" />
      <div className="mt-2 rounded-2xl border border-white/10 bg-black/30 overflow-hidden flex-1" data-testid="tv-tabela-recebimentos">
        <div className="grid gap-3 items-center px-4 py-4 bg-white/10 uppercase tracking-widest text-[17px] font-bold text-white/80"
             style={{ gridTemplateColumns: cols }}>
          <div className="text-center">EMP</div>
          <div className="text-center">Data</div>
          <div className="text-center">Dia</div>
          <div className="text-center">Horário</div>
          <div className="text-center">Fornecedor</div>
          <div className="text-center whitespace-nowrap">Nota fiscal</div>
          <div className="text-center">SKU</div>
          <div className="text-center">KG</div>
          <div className="text-center">Vol.</div>
          <div className="text-center">Paletes</div>
          <div className="text-center">Status</div>
          <div className="text-center">Observações</div>
        </div>
        <div className="divide-y divide-white/10">
          {visible.length === 0 && (
            <div className="px-4 py-16 text-center text-3xl text-white/60">Nenhum recebimento hoje</div>
          )}
          {visible.map((r, i) => (
            <div key={r.id}
              className={`grid gap-3 items-center px-4 py-4 text-[24px] font-medium ${r.destacar_tv ? "bg-amber-300 text-slate-900" : (i % 2 === 0 ? "bg-white/5" : "bg-transparent")}`}
              style={{ gridTemplateColumns: cols }}
              data-testid={`tv-rec-row-${r.id}`}>
              <div className="text-center font-bold tabular-nums" data-testid={`tv-rec-emp-${r.id}`}>{r.emp || "—"}</div>
              <div className="text-center tabular-nums">
                {fmtDataBR(r.data)}
                <OntemChip label={labelDiaAnterior(r.data, hoje, diaAntIso)} testid={`tv-rec-ontem-${r.id}`} />
              </div>
              <div className="text-center truncate">{r.dia}</div>
              <div className="text-center">{r.horario}</div>
              <div className="text-center font-bold truncate" title={r.fornecedor_nome}>{r.fornecedor_nome}</div>
              <div className="text-center truncate tabular-nums">{r.nota_fiscal || "—"}</div>
              <div className="text-center truncate">{r.sku || "—"}</div>
              <div className="text-center tabular-nums">{r.kg}</div>
              <div className="text-center tabular-nums">{r.volumes}</div>
              <div className="text-center tabular-nums">{r.paletes}</div>
              <div className="text-center"><StatusPill status={r.status} /></div>
              <div className="text-center truncate italic opacity-90" title={r.observacoes}>{r.observacoes || "—"}</div>
            </div>
          ))}
        </div>
      </div>
      {pages > 1 && (
        <div className="text-right text-lg mt-2 opacity-80" data-testid="tv-rec-pag">Página {p + 1} de {pages}</div>
      )}
    </div>
  );
}

function EnviosSlide({ rows, page, onPagesChange, hoje, diaAntIso }) {
  const { perPage, pages } = useAutoPagination(rows, 92, 380);
  useEffect(() => { onPagesChange(pages); }, [pages, onPagesChange]);
  const p = page % pages;
  const visible = rows.slice(p * perPage, p * perPage + perPage);

  const cols = "120px 180px minmax(400px, 2.5fr) 240px minmax(240px, 1.6fr)";

  return (
    <div className="w-full h-full px-8 pt-2 pb-28 flex flex-col text-white">
      <SlideTitleWithTruck label="ENVIOS" direction="out-left" dataTestidPrefix="tv-env" />
      <div className="mt-2 rounded-2xl border border-white/10 bg-black/30 overflow-hidden flex-1" data-testid="tv-tabela-envios">
        <div className="grid gap-3 items-center px-4 py-4 bg-white/10 uppercase tracking-widest text-[17px] font-bold text-white/80"
             style={{ gridTemplateColumns: cols }}>
          <div className="text-center">EMP</div>
          <div className="text-center">Data</div>
          <div className="text-center">Transportadora</div>
          <div className="text-center">Qtd. de notas</div>
          <div className="text-center">Observação</div>
        </div>
        <div className="divide-y divide-white/10">
          {visible.length === 0 && (
            <div className="px-4 py-16 text-center text-3xl text-white/60">Nenhum envio hoje</div>
          )}
          {visible.map((e, i) => (
            <div key={e.id}
              className={`grid gap-3 items-center px-4 py-5 text-[26px] font-medium ${e.destacar_tv ? "bg-amber-300 text-slate-900" : (i % 2 === 0 ? "bg-white/5" : "bg-transparent")}`}
              style={{ gridTemplateColumns: cols }}
              data-testid={`tv-env-row-${e.id}`}>
              <div className="text-center font-bold tabular-nums" data-testid={`tv-env-emp-${e.id}`}>{e.emp || "—"}</div>
              <div className="text-center tabular-nums">
                {fmtDataBR(e.data)}
                <OntemChip label={labelDiaAnterior(e.data, hoje, diaAntIso)} testid={`tv-env-ontem-${e.id}`} />
              </div>
              <div className="text-center font-bold truncate" title={e.transportadora_nome}>{e.transportadora_nome}</div>
              <div className="text-center tabular-nums text-4xl font-black">{e.quantidade_notas}</div>
              <div className="text-center truncate italic opacity-90" title={e.observacao}>{e.observacao || "—"}</div>
            </div>
          ))}
        </div>
      </div>
      {pages > 1 && (
        <div className="text-right text-lg mt-2 opacity-80" data-testid="tv-env-pag">Página {p + 1} de {pages}</div>
      )}
    </div>
  );
}

function AvisosSlide({ rows, page, onPagesChange }) {
  // Cada card grande ocupa ~280px com fonte 5xl+6xl e validade — usamos essa
  // altura estimada para garantir que nenhum card fique cortado.
  const { perPage, pages } = useAutoPagination(rows, 280, 340);
  useEffect(() => { onPagesChange(pages); }, [pages, onPagesChange]);
  const p = page % pages;
  const visible = rows.slice(p * perPage, p * perPage + perPage);

  return (
    <div className="w-full h-full px-8 pt-2 pb-28 flex flex-col text-white">
      <div className="relative w-full h-32 flex items-center justify-center">
        <h1 className="text-7xl xl:text-8xl font-black tracking-wide flex items-center gap-5">
          <Megaphone className="w-16 h-16" /> <span>AVISOS</span>
        </h1>
      </div>
      <div className="flex-1 flex flex-col items-center justify-start gap-5 mt-4 overflow-hidden" data-testid="tv-avisos-container">
        {visible.map((a) => (
          <div key={a.id}
            className="bg-amber-300 text-slate-900 rounded-3xl px-10 py-6 max-w-6xl w-full text-center shadow-2xl"
            data-testid={`tv-aviso-${a.id}`}>
            <div className="text-5xl xl:text-6xl font-bold leading-snug whitespace-pre-wrap break-words">{a.mensagem}</div>
            <div className="text-2xl mt-3 opacity-80">Válido até {fmtDataBR(a.valido_ate)}</div>
          </div>
        ))}
      </div>
      {pages > 1 && (
        <div className="text-right text-lg mt-2 opacity-80" data-testid="tv-aviso-pag">Página {p + 1} de {pages}</div>
      )}
    </div>
  );
}

// Relógio com dois pontos piscando (1s).
function ClockTopo({ now }) {
  const [blink, setBlink] = useState(true);
  useEffect(() => {
    const t = setInterval(() => setBlink((b) => !b), 1000);
    return () => clearInterval(t);
  }, []);
  const hh = String(now.getHours()).padStart(2, "0");
  const mm = String(now.getMinutes()).padStart(2, "0");
  return (
    <div className="text-7xl xl:text-8xl font-black tabular-nums text-navy" data-testid="tv-relogio">
      <span>{hh}</span>
      <span style={{ opacity: blink ? 1 : 0, transition: "opacity 0.15s linear" }}>:</span>
      <span>{mm}</span>
    </div>
  );
}

export default function TVLogistica() {
  const { logout } = useAuth();
  const [feed, setFeed] = useState({ recebimentos: [], envios: [], avisos: [], hoje: null, dia_util_anterior_iso: null });
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(() => new Date());
  const [slideIdx, setSlideIdx] = useState(0);
  const [pageInSlide, setPageInSlide] = useState(0);
  const [pagesPerSlide, setPagesPerSlide] = useState({ recebimentos: 1, envios: 1, avisos: 1 });

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get("/logistica/feed");
        if (!alive) return;
        setFeed({
          recebimentos: data?.recebimentos || [],
          envios: data?.envios || [],
          avisos: data?.avisos || [],
          hoje: data?.hoje || null,
          dia_util_anterior_iso: data?.dia_util_anterior_iso || null,
        });
      } catch { /* mantém último ok */ }
      finally { if (alive) setLoading(false); }
    };
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => { alive = false; clearInterval(t); };
  }, []);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const activeSlides = useMemo(() => {
    // LOG-TV-HOJE — Recebimentos e Envios aparecem sempre (mostram "Nenhum X hoje" quando vazios).
    const list = ["recebimentos", "envios"];
    if (feed.avisos.length > 0) list.push("avisos");
    return list;
  }, [feed]);

  // CORREÇÃO L-1 (08/10): o timer do carrossel NÃO pode reiniciar quando o
  // feed atualiza (a cada 15 s). Lemos `activeSlides`/`slideIdx`/`pageInSlide`/
  // `pagesPerSlide` por `useRef` dentro do callback do `setInterval`, e o
  // efeito depende apenas de uma CHAVE PRIMITIVA estável (quantidade de slides)
  // + do próprio PAGE_MS. Assim, o setInterval sobrevive ao refetch do feed.
  const slidesRef = useRef(activeSlides);
  const slideIdxRef = useRef(slideIdx);
  const pageInSlideRef = useRef(pageInSlide);
  const pagesRef = useRef(pagesPerSlide);
  useEffect(() => { slidesRef.current = activeSlides; }, [activeSlides]);
  useEffect(() => { slideIdxRef.current = slideIdx; }, [slideIdx]);
  useEffect(() => { pageInSlideRef.current = pageInSlide; }, [pageInSlide]);
  useEffect(() => { pagesRef.current = pagesPerSlide; }, [pagesPerSlide]);

  const slidesCount = activeSlides.length;
  useEffect(() => {
    if (slidesCount === 0) return;
    const t = setInterval(() => {
      const slides = slidesRef.current;
      if (!slides.length) return;
      const key = slides[slideIdxRef.current % slides.length];
      const pages = pagesRef.current[key] || 1;
      if (pageInSlideRef.current + 1 < pages) {
        setPageInSlide((prev) => prev + 1);
      } else {
        setPageInSlide(0);
        setSlideIdx((i) => (i + 1) % (slidesRef.current.length || 1));
      }
    }, PAGE_MS);
    return () => clearInterval(t);
  }, [slidesCount]);

  useEffect(() => {
    // Se a lista de slides diminuir (ex.: avisos expiraram), ajusta o índice
    // sem zerar o timer (não refaz o setInterval acima — só corrige o cursor).
    if (slideIdx >= activeSlides.length && activeSlides.length > 0) {
      setSlideIdx(0); setPageInSlide(0);
    }
  }, [activeSlides.length, slideIdx]);

  const dataHoje = useMemo(() => fmtDataHojePt(now), [now]);
  const currentSlide = activeSlides[slideIdx % activeSlides.length];

  return (
    <div className="w-screen h-screen bg-gradient-to-br from-navy via-slate-900 to-brand text-white overflow-hidden relative">
      {/* LOTE L-1 (item 1): botão "Sair" discreto no canto superior direito.
          Fica sempre visível (opacidade baixa), fica totalmente visível ao
          hover/focus; funciona com mouse e com toque. z-30 para ficar acima
          da faixa branca do topo. */}
      <button
        type="button"
        onClick={() => logout()}
        data-testid="tv-logout"
        aria-label="Sair"
        className="absolute top-5 right-6 z-30 inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-black/40 text-white/80 text-xs font-medium hover:bg-black/70 hover:text-white focus:bg-black/70 focus:text-white backdrop-blur-sm transition-colors"
      >
        <LogOut className="w-4 h-4" /> Sair
      </button>
      {/* Faixa do topo branca — ~152px de altura (py-4 + logo 140px). O logo
          usa `/mpires-logo-tv.png` (recorte 287×229 do original) com altura
          fixa de 140px (faixa − 12px), sem borda/quadro. */}
      <div className="absolute top-0 inset-x-0 flex items-center justify-between px-10 py-4 z-10 bg-white shadow-md" data-testid="tv-topo-branco">
        <div className="flex items-center gap-6">
          <img src="/mpires-logo-tv.png" alt="MPires Connect" style={{ height: 140, width: "auto" }} data-testid="tv-logo" />
          <div>
            <div className="text-base uppercase tracking-widest text-slate-500 font-semibold">Módulo Logística</div>
            <div className="text-3xl xl:text-4xl font-bold text-navy" data-testid="tv-data-topo">{dataHoje}</div>
          </div>
        </div>
        <ClockTopo now={now} />
      </div>

      <div className="w-full h-full pt-44">
        {loading && (
          <div className="w-full h-full flex flex-col items-center justify-center gap-3 opacity-70">
            <Loader2 className="w-10 h-10 animate-spin" />
            <div className="text-lg">Carregando painel...</div>
          </div>
        )}
        {!loading && activeSlides.length === 0 && (
          <div className="w-full h-full flex items-center justify-center text-center opacity-70">
            <div>
              <div className="text-3xl font-bold">Nenhum registro no momento</div>
              <div className="text-lg mt-2">Aguardando o Adm Logístico cadastrar Recebimentos, Envios ou Avisos.</div>
            </div>
          </div>
        )}
        {!loading && currentSlide === "recebimentos" && (
          <RecebimentosSlide rows={feed.recebimentos} page={pageInSlide}
            hoje={feed.hoje} diaAntIso={feed.dia_util_anterior_iso}
            onPagesChange={(n) => setPagesPerSlide((s) => (s.recebimentos === n ? s : { ...s, recebimentos: n }))} />
        )}
        {!loading && currentSlide === "envios" && (
          <EnviosSlide rows={feed.envios} page={pageInSlide}
            hoje={feed.hoje} diaAntIso={feed.dia_util_anterior_iso}
            onPagesChange={(n) => setPagesPerSlide((s) => (s.envios === n ? s : { ...s, envios: n }))} />
        )}
        {!loading && currentSlide === "avisos" && (
          <AvisosSlide rows={feed.avisos} page={pageInSlide}
            onPagesChange={(n) => setPagesPerSlide((s) => (s.avisos === n ? s : { ...s, avisos: n }))} />
        )}
      </div>

      {activeSlides.length > 1 && (
        <div className="absolute bottom-6 inset-x-0 flex items-center justify-center gap-2 z-10">
          {activeSlides.map((s, i) => (
            <div key={s} className={`h-1.5 rounded-full transition-all ${i === slideIdx % activeSlides.length ? "w-12 bg-white" : "w-2 bg-white/30"}`} />
          ))}
        </div>
      )}
      <div className="absolute bottom-6 right-8 text-xs opacity-50" data-testid="tv-slide-info">
        {activeSlides.length > 0 ? `${currentSlide?.toUpperCase()} · ${slideIdx + 1}/${activeSlides.length}` : ""}
      </div>
    </div>
  );
}
